import React from 'react';
import { ArrowRight, Shield, CheckCircle, AlertCircle, Info, Clock, Droplets, AlertTriangle, Calendar, Wrench, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const CertificationPage: React.FC = () => {
  const navigate = useNavigate();
  
  const scrollToCertifications = () => {
    const element = document.getElementById('certificaciones-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-[#061970] h-[700px] overflow-hidden">
        <div className="absolute inset-0 opacity-10" />
        <div className="absolute inset-0 opacity-30">
          <img 
            src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1400&q=80" 
            alt="Casco certificado con etiquetas DOT y ECE" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#061970] via-[#061970]/95 to-[#061970]/80" />
        
        {/* Content - Expanded laterally */}
        <div className="relative h-full flex items-center">
          <div className="w-full px-8 md:px-16 lg:px-24">
            <div className="max-w-[800px]">
              <div className="inline-flex items-center gap-2 bg-[#459edf]/20 border border-[#459edf]/30 rounded-full px-4 py-2 mb-8 w-fit">
                <Shield className="w-4 h-4 text-[#459edf]" />
                <span className="text-[#459edf] text-sm font-bold uppercase tracking-wider">Certificación Oficial</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black italic text-white uppercase tracking-tighter leading-[1.1] mb-6">
                Cascos de moto certificados: <span className="text-[#ff7f32]">Seguridad</span> y <span className="text-[#459edf]">Protección</span> en Movimiento
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-200 mb-12 tracking-wide">
                Tu casco es tu inversión más importante
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => navigate('/products')}
                  className="bg-[#ff7f32] text-white px-8 py-4 rounded-full font-bold italic uppercase tracking-wide shadow-lg hover:bg-[#e66a20] transition-colors flex items-center justify-center gap-2 group"
                >
                  Ver Catálogo Certificado
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" className="w-5 h-5 group-hover:translate-x-1 transition-transform" >
                    <path d="M4.16699 10H15.8337" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M10 4.16669L15.8333 10L10 15.8334" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <button 
                  onClick={scrollToCertifications}
                  className="bg-white/10 border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold italic uppercase tracking-wide hover:bg-white/20 transition-colors flex items-center justify-center gap-2 group"
                >
                  Conocer Estándares
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" className="w-5 h-5 group-hover:scale-110 transition-transform">
                    <path d="M7.5 15L12.5 10L7.5 5" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Certification Badges - Positioned on the right */}
        <div className="absolute bottom-16 right-8 md:right-16 lg:right-24 flex gap-4">
          <div className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-sm w-36">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none" className="w-12 h-12 text-white mx-auto mb-3">
                <path d="M30.9545 25.78L33.9845 42.832C34.0184 43.0328 33.9902 43.2392 33.9037 43.4235C33.8172 43.6079 33.6765 43.7614 33.5003 43.8636C33.3242 43.9658 33.1211 44.0118 32.9181 43.9954C32.7151 43.9791 32.522 43.9011 32.3645 43.772L25.2045 38.398C24.8588 38.1398 24.4389 38.0003 24.0075 38.0003C23.576 38.0003 23.1561 38.1398 22.8105 38.398L15.6385 43.77C15.4811 43.8989 15.2882 43.9767 15.0855 43.9931C14.8827 44.0095 14.6798 43.9637 14.5038 43.8618C14.3278 43.7598 14.187 43.6067 14.1003 43.4227C14.0136 43.2387 13.9851 43.0327 14.0185 42.832L17.0465 25.78" stroke="#FF7F32" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M24 28C30.6274 28 36 22.6274 36 16C36 9.37258 30.6274 4 24 4C17.3726 4 12 9.37258 12 16C12 22.6274 17.3726 28 24 28Z" stroke="#FF7F32" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <p className="text-white text-lg font-bold text-center">DOT</p>
            <p className="text-gray-300 text-xs text-center uppercase tracking-wider">Certificado</p>
          </div>
          <div className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-sm w-36">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none" className="w-12 h-12 text-white mx-auto mb-3">
                <path d="M40 26C40 36 33 41 24.68 43.9C24.2443 44.0476 23.7711 44.0406 23.34 43.88C15 41 8 36 8 26V12C8 11.4696 8.21071 10.9608 8.58579 10.5858C8.96086 10.2107 9.46957 9.99998 10 9.99998C14 9.99998 19 7.59998 22.48 4.55998C22.9037 4.19798 23.4427 3.99908 24 3.99908C24.5573 3.99908 25.0963 4.19798 25.52 4.55998C29.02 7.61998 34 9.99998 38 9.99998C38.5304 9.99998 39.0391 10.2107 39.4142 10.5858C39.7893 10.9608 40 11.4696 40 12V26Z" stroke="#459EDF" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <p className="text-white text-lg font-bold text-center">ECE 22.06</p>
            <p className="text-gray-300 text-xs text-center uppercase tracking-wider">Aprobado</p>
          </div>
        </div>
      </section>

      {/* Why Certification Matters */}
      <section className="bg-[#f9fafb] py-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black italic text-[#061970] uppercase tracking-tighter mb-4">
              ¿Por qué importa la certificación?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Un casco certificado es la diferencia entre una protección real y una falsa sensación de seguridad
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* Image */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl h-96">
              <img 
                src="/cert-casco2.jpg" 
                alt="Pruebas de impacto en casco" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#061970]/60 to-transparent" />
              <p className="absolute bottom-6 left-6 text-white font-bold italic text-lg">
                Pruebas de impacto real en laboratorio certificado
              </p>
            </div>
            
            {/* Features */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex gap-4">
                  <div className="bg-[#ff7f32]/10 rounded-2xl p-3 h-fit">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-[#ff7f32]">
                    <path d="M4.00048 14C3.81125 14.0006 3.62571 13.9476 3.46543 13.847C3.30516 13.7464 3.17671 13.6023 3.09503 13.4316C3.01335 13.2609 2.98177 13.0706 3.00398 12.8826C3.02619 12.6947 3.10126 12.5169 3.22048 12.37L13.1205 2.16998C13.1947 2.08426 13.2959 2.02634 13.4075 2.00571C13.519 1.98509 13.6342 2.00299 13.7342 2.05648C13.8342 2.10998 13.9131 2.19588 13.9578 2.30009C14.0026 2.4043 14.0106 2.52063 13.9805 2.62998L12.0605 8.64998C12.0039 8.8015 11.9849 8.9645 12.0051 9.12499C12.0253 9.28547 12.0841 9.43866 12.1766 9.57141C12.269 9.70415 12.3923 9.81249 12.5358 9.88714C12.6793 9.96179 12.8387 10.0005 13.0005 9.99998H20.0005C20.1897 9.99933 20.3752 10.0524 20.5355 10.153C20.6958 10.2536 20.8242 10.3976 20.9059 10.5683C20.9876 10.739 21.0192 10.9294 20.997 11.1173C20.9748 11.3053 20.8997 11.483 20.7805 11.63L10.8805 21.83C10.8062 21.9157 10.705 21.9736 10.5935 21.9942C10.482 22.0149 10.3668 21.997 10.2668 21.9435C10.1667 21.89 10.0879 21.8041 10.0431 21.6999C9.9984 21.5957 9.9904 21.4793 10.0205 21.37L11.9405 15.35C11.9971 15.1985 12.0161 15.0355 11.9959 14.875C11.9757 14.7145 11.9168 14.5613 11.8244 14.4286C11.732 14.2958 11.6087 14.1875 11.4652 14.1128C11.3217 14.0382 11.1622 13.9995 11.0005 14H4.00048Z" stroke="#FF7F32" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#061970] mb-2">Absorción de Energía</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Los cascos certificados están probados para absorber la energía de impactos reales, protegiendo tu cabeza en escenarios críticos de carretera y ciudad.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex gap-4">
                  <div className="bg-[#459edf]/10 rounded-2xl p-3 h-fit">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-[#459edf]">
                    <path d="M12.8305 2.17999C12.5699 2.06114 12.2868 1.99963 12.0005 1.99963C11.7141 1.99963 11.431 2.06114 11.1705 2.17999L2.60045 6.07999C2.423 6.15823 2.27213 6.28639 2.16621 6.44885C2.0603 6.61131 2.00391 6.80106 2.00391 6.99499C2.00391 7.18893 2.0603 7.37868 2.16621 7.54113C2.27213 7.70359 2.423 7.83175 2.60045 7.90999L11.1805 11.82C11.441 11.9388 11.7241 12.0003 12.0105 12.0003C12.2968 12.0003 12.5799 11.9388 12.8405 11.82L21.4205 7.91999C21.5979 7.84175 21.7488 7.71359 21.8547 7.55113C21.9606 7.38868 22.017 7.19893 22.017 7.00499C22.017 6.81106 21.9606 6.6213 21.8547 6.45885C21.7488 6.29639 21.5979 6.16823 21.4205 6.08999L12.8305 2.17999Z" stroke="#459EDF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M2 12C1.99953 12.1913 2.05392 12.3787 2.15672 12.5399C2.25952 12.7012 2.40642 12.8297 2.58 12.91L11.18 16.82C11.4392 16.9374 11.7205 16.9981 12.005 16.9981C12.2895 16.9981 12.5708 16.9374 12.83 16.82L21.41 12.92C21.587 12.8404 21.737 12.7111 21.8418 12.5477C21.9466 12.3844 22.0015 12.1941 22 12" stroke="#459EDF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M2 17C1.99953 17.1913 2.05392 17.3787 2.15672 17.5399C2.25952 17.7012 2.40642 17.8297 2.58 17.91L11.18 21.82C11.4392 21.9374 11.7205 21.9981 12.005 21.9981C12.2895 21.9981 12.5708 21.9374 12.83 21.82L21.41 17.92C21.587 17.8404 21.737 17.7111 21.8418 17.5477C21.9466 17.3844 22.0015 17.1941 22 17" stroke="#459EDF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#061970] mb-2">Resistencia Estructural</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Las pruebas de penetración y deformación garantizan que el casco mantenga su integridad bajo presión extrema, protegiendo contra objetos punzantes.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex gap-4">
                  <div className="bg-[#ff7f32]/10 rounded-2xl p-3 h-fit">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-[#ff7f32]" >
                    <path d="M21.8006 9.99999C22.2573 12.2413 21.9318 14.5714 20.8785 16.6018C19.8251 18.6322 18.1075 20.24 16.0121 21.1573C13.9167 22.0746 11.5702 22.2458 9.36391 21.6424C7.15758 21.0389 5.2248 19.6974 3.88789 17.8414C2.55097 15.9854 1.89073 13.7272 2.01728 11.4434C2.14382 9.15952 3.04949 6.98808 4.58326 5.29116C6.11703 3.59424 8.18619 2.47442 10.4457 2.11844C12.7052 1.76247 15.0184 2.19185 16.9996 3.33499" stroke="#FF7F32" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M9 11L12 14L22 4" stroke="#FF7F32" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#061970] mb-2">Seguridad Verificada</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Cada certificación representa cientos de horas de pruebas independientes, asegurando que tu casco cumple con estándares internacionales de seguridad.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DOT and ECE Comparison */}
      <section className="bg-[#061970] py-20 overflow-hidden relative" id="certificaciones-section">
        <div className="absolute inset-0 opacity-5" />
        
        <div className="container mx-auto px-4 max-w-7xl relative">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black italic text-white uppercase tracking-tighter mb-4">
              Certificación DOT y ECE 22.06: <span className="text-[#459edf]">Protección real</span>
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Los dos estándares más rigurosos del mundo para casco de moto seguro, casco para carretera y casco urbano
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* DOT Card */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-2xl">
              <div className="bg-gradient-to-b from-[#ff7f32] to-[#e66a20] p-8 text-white">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none" className="w-16 h-16 mb-4">
                <path d="M41.2723 34.3733L45.3123 57.1093C45.3576 57.377 45.32 57.6522 45.2046 57.898C45.0893 58.1438 44.9016 58.3485 44.6668 58.4847C44.432 58.621 44.1611 58.6823 43.8905 58.6605C43.6198 58.6387 43.3623 58.5348 43.1523 58.3626L33.6056 51.1973C33.1448 50.853 32.5849 50.6669 32.0096 50.6669C31.4344 50.6669 30.8745 50.853 30.4136 51.1973L20.851 58.36C20.6412 58.5318 20.3839 58.6355 20.1136 58.6574C19.8433 58.6793 19.5728 58.6182 19.3381 58.4823C19.1034 58.3464 18.9157 58.1422 18.8001 57.8968C18.6845 57.6515 18.6464 57.3768 18.691 57.1093L22.7283 34.3733" stroke="white" stroke-width="5.33333" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M32 37.3334C40.8366 37.3334 48 30.1699 48 21.3334C48 12.4968 40.8366 5.33337 32 5.33337C23.1634 5.33337 16 12.4968 16 21.3334C16 30.1699 23.1634 37.3334 32 37.3334Z" stroke="white" stroke-width="5.33333" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <h3 className="text-3xl font-black italic uppercase mb-2">DOT</h3>
                <p className="text-white/90 font-bold text-lg">Department of Transportation</p>
              </div>
              
              <div className="p-8 space-y-6">
                <p className="text-gray-600 leading-relaxed">
                  El estándar DOT (FMVSS 218) es la certificación obligatoria para cascos de motocicleta en Estados Unidos y referencia global para la <span className="font-bold text-gray-800">seguridad en carretera</span>.
                </p>
                
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#ff7f32] flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">
                      <span className="font-bold">Prueba de absorción de impacto:</span> El casco debe reducir la fuerza transmitida al cráneo a menos de 400G.
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#ff7f32] flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">
                      <span className="font-bold">Prueba de penetración:</span> Resistencia contra objetos puntiagudos desde altura de caída libre.
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#ff7f32] flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">
                      <span className="font-bold">Sistema de retención:</span> Las correas deben soportar mínimo 226 kg de fuerza sin romperse.
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#ff7f32] flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">
                      <span className="font-bold">Campo visual:</span> Mínimo 105° de visión lateral para seguridad en tráfico urbano.
                    </p>
                  </div>
                </div>
                
                <div className="bg-[#ff7f32]/10 border-l-4 border-[#ff7f32] rounded-r-2xl p-4">
                  <p className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-1">Ideal para</p>
                  <p className="text-gray-700">Motociclistas urbanos, uso diario, y conducción en carretera en América.</p>
                </div>
              </div>
            </div>
            
            {/* ECE Card */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-2xl">
              <div className="bg-gradient-to-b from-[#459edf] to-[#3a7fb8] p-8 text-white">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none" className="w-16 h-16 mb-4">
                <path d="M53.3337 34.6667C53.3337 48 44.0003 54.6667 32.907 58.5334C32.3261 58.7302 31.6951 58.7208 31.1203 58.5067C20.0003 54.6667 10.667 48 10.667 34.6667V16C10.667 15.2928 10.9479 14.6145 11.448 14.1144C11.9481 13.6143 12.6264 13.3334 13.3337 13.3334C18.667 13.3334 25.3337 10.1334 29.9737 6.08002C30.5386 5.59735 31.2573 5.33215 32.0003 5.33215C32.7434 5.33215 33.462 5.59735 34.027 6.08002C38.6937 10.16 45.3337 13.3334 50.667 13.3334C51.3742 13.3334 52.0525 13.6143 52.5526 14.1144C53.0527 14.6145 53.3337 15.2928 53.3337 16V34.6667Z" stroke="white" stroke-width="5.33333" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <h3 className="text-3xl font-black italic uppercase mb-2">ECE 22.06</h3>
                <p className="text-white/90 font-bold text-lg">Economic Commission for Europe</p>
              </div>
              
              <div className="p-8 space-y-6">
                <p className="text-gray-600 leading-relaxed">
                  La certificación <span className="font-bold text-gray-800">ECE 22.06</span> es considerada el estándar más avanzado y riguroso del mundo para <span className="font-bold text-gray-800">cascos integrales, modulares y jet</span>, vigente desde 2022.
                </p>
                
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#459edf] flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">
                      <span className="font-bold">Impacto multi-punto:</span> Pruebas en 5 zonas diferentes del casco a velocidades variables (7.5 m/s).
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#459edf] flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">
                      <span className="font-bold">Impacto rotacional:</span> Evalúa lesiones cerebrales causadas por rotación angular del casco.
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#459edf] flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">
                      <span className="font-bold">Absorción dual:</span> Pruebas en yunques planos y con bordes (simulando impactos contra bordillos).
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#459edf] flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">
                      <span className="font-bold">Viseras avanzadas:</span> Resistencia óptica, anti-rayado y pruebas de desprendimiento en impactos.
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#459edf] flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">
                      <span className="font-bold">Protección mentonera:</span> Obligatoria en cascos integrales con pruebas específicas de impacto frontal.
                    </p>
                  </div>
                </div>
                
                <div className="bg-[#459edf]/10 border-l-4 border-[#459edf] rounded-r-2xl p-4">
                  <p className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-1">Ideal para</p>
                  <p className="text-gray-700">Motociclistas profesionales, touring de larga distancia, y máxima protección integral.</p>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </section>

      {/* Maintenance and Lifecycle */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black italic text-[#061970] uppercase tracking-tighter mb-4">
              Mantenimiento y <span className="text-[#459edf]">Ciclo de Vida</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Un casco bien cuidado es un casco que salva vidas. Conoce cómo mantener tu protección en óptimas condiciones.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* 5 Years */}
            <div className="bg-[#f9fafb] border-2 border-gray-200 rounded-2xl p-8 text-center">
              <div className="bg-[#ff7f32] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <Calendar className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold italic text-[#061970] uppercase mb-4">3 Años de Vida</h3>
              <p className="text-gray-600 mb-6">
                La vida útil máxima de un casco es de <span className="font-bold text-gray-800">3 años desde la fecha de fabricación</span>, independientemente del uso.
              </p>
              <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-start gap-3">
                <Info className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700 text-left">
                  Materiales como el EPS se degradan con el tiempo, reduciendo la capacidad de absorción.
                </p>
              </div>
            </div>
            
            {/* Cleaning */}
            <div className="bg-[#f9fafb] border-2 border-gray-200 rounded-2xl p-8 text-center">
              <div className="bg-[#459edf] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <Droplets className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold italic text-[#061970] uppercase mb-4">Limpieza del Forro</h3>
              <p className="text-gray-600 mb-6">
                Lava el <span className="font-bold text-gray-800">forro interno cada 2-3 meses</span> con jabón neutro y agua tibia. Nunca uses químicos agresivos.
              </p>
              <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-[#459edf] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700 text-left">
                  Un interior limpio previene bacterias y prolonga la comodidad del casco.
                </p>
              </div>
            </div>
            
            {/* Replacement */}
            <div className="bg-[#f9fafb] border-2 border-gray-200 rounded-2xl p-8 text-center">
              <div className="bg-[#ff7f32] rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold italic text-[#061970] uppercase mb-4">Reemplazo Obligatorio</h3>
              <p className="text-gray-600 mb-6">
                <span className="font-bold text-gray-800">Reemplaza tu casco inmediatamente</span> después de cualquier impacto significativo, incluso si no hay daños visibles.
              </p>
              <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-start gap-3">
                <Info className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700 text-left">
                  La estructura interna puede estar comprometida sin señales externas.
                </p>
              </div>
            </div>
          </div>
          
          {/* Max the Bear Tip */}
          <div className="bg-gradient-to-b from-[#459edf] to-[#3a7fb8] rounded-3xl p-8 md:p-12 text-white relative overflow-hidden shadow-2xl">
            <div className="absolute inset-0 opacity-10" />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" className="w-4 h-4">
                    <g clip-path="url(#clip0_300_5767)">
                        <path d="M13.3337 8.66664C13.3337 12 11.0003 13.6666 8.22699 14.6333C8.08177 14.6825 7.92402 14.6802 7.78033 14.6266C5.00033 13.6666 2.66699 12 2.66699 8.66664V3.99997C2.66699 3.82316 2.73723 3.65359 2.86225 3.52857C2.98728 3.40355 3.15685 3.33331 3.33366 3.33331C4.66699 3.33331 6.33366 2.53331 7.49366 1.51997C7.6349 1.39931 7.81456 1.33301 8.00033 1.33301C8.18609 1.33301 8.36576 1.39931 8.50699 1.51997C9.67366 2.53997 11.3337 3.33331 12.667 3.33331C12.8438 3.33331 13.0134 3.40355 13.1384 3.52857C13.2634 3.65359 13.3337 3.82316 13.3337 3.99997V8.66664Z" stroke="white" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_300_5767">
                        <rect width="16" height="16" fill="white"/>
                        </clipPath>
                    </defs>
                    </svg>
                  <span className="text-sm font-bold uppercase tracking-wider">Consejo de Max</span>
                </div>
                
                <p className="text-2xl font-bold mb-6 leading-relaxed">
                  Max el Oso te recuerda: <span className="font-normal">Revisa tus correas de seguridad cada semana. Un ajuste correcto debe permitir pasar </span>solo dos dedos<span className="font-normal"> entre la correa y tu mentón.</span>
                </p>
                
                <div className="space-y-3">
                  <div className="bg-white/10 rounded-xl p-4 flex items-start gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" className="w-5 h-5 flex-shrink-0 mt-0.5">
                    <path d="M18.1678 8.33332C18.5484 10.2011 18.2772 12.1428 17.3994 13.8348C16.5216 15.5268 15.0902 16.8667 13.3441 17.6311C11.5979 18.3955 9.64252 18.5381 7.80391 18.0353C5.9653 17.5325 4.35465 16.4145 3.24056 14.8678C2.12646 13.3212 1.57626 11.4394 1.68171 9.53615C1.78717 7.63294 2.54189 5.8234 3.82004 4.4093C5.09818 2.9952 6.82248 2.06202 8.70538 1.76537C10.5883 1.46872 12.516 1.82654 14.167 2.77916" stroke="#FF7F32" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M7.5 9.16659L10 11.6666L18.3333 3.33325" stroke="#FF7F32" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <p>Correas demasiado flojas = casco puede salir volando en un impacto</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 flex items-start gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" className="w-5 h-5 flex-shrink-0 mt-0.5">
                    <path d="M18.1678 8.33332C18.5484 10.2011 18.2772 12.1428 17.3994 13.8348C16.5216 15.5268 15.0902 16.8667 13.3441 17.6311C11.5979 18.3955 9.64252 18.5381 7.80391 18.0353C5.9653 17.5325 4.35465 16.4145 3.24056 14.8678C2.12646 13.3212 1.57626 11.4394 1.68171 9.53615C1.78717 7.63294 2.54189 5.8234 3.82004 4.4093C5.09818 2.9952 6.82248 2.06202 8.70538 1.76537C10.5883 1.46872 12.516 1.82654 14.167 2.77916" stroke="#FF7F32" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M7.5 9.16659L10 11.6666L18.3333 3.33325" stroke="#FF7F32" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <p>Correas muy apretadas = fatiga y dolor en viajes largos</p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-center">
                <img 
                  src="/MaxMRC1.png" 
                  alt="Max el Oso" 
                  className="w-94 h-auto object-contain drop-shadow-2xl"
                />
              </div>
            </div>
          </div>
          
          {/* Monthly Checklist */}
          <div className="mt-12 bg-[#f9fafb] border-2 border-gray-200 rounded-2xl p-8">
            <h3 className="text-2xl font-bold italic text-[#061970] uppercase text-center mb-8">
              Checklist de Mantenimiento Mensual
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#459edf] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">Inspecciona la carcasa exterior en busca de grietas o deformaciones</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#459edf] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">Verifica el estado de la visera (rayones, opacidad, mecanismo de apertura)</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#459edf] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">Revisa las correas de retención y hebillas de cierre</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#459edf] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">Limpia los conductos de ventilación con aire comprimido</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#459edf] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">Confirma que el forro interior esté correctamente adherido</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#459edf] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700">Guarda el casco en lugar fresco, seco y alejado de luz solar directa</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-b from-[#061970] to-[#061970] via-[#0a2580] py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" />
        
        <div className="container mx-auto px-4 max-w-4xl text-center relative">
          <div className="inline-flex items-center gap-2 bg-[#ff7f32]/20 border border-[#ff7f32]/30 rounded-full px-4 py-2 mb-8">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" className="w-4 h-4 text-[#ff7f32]">
            <g clip-path="url(#clip0_300_5797)">
                <path d="M10.3178 8.59326L11.3278 14.2773C11.3391 14.3442 11.3298 14.413 11.3009 14.4744C11.2721 14.5359 11.2252 14.5871 11.1665 14.6211C11.1077 14.6552 11.04 14.6705 10.9724 14.6651C10.9047 14.6596 10.8403 14.6336 10.7878 14.5906L8.40117 12.7993C8.28595 12.7132 8.14599 12.6667 8.00217 12.6667C7.85835 12.6667 7.71838 12.7132 7.60317 12.7993L5.2125 14.5899C5.16005 14.6329 5.09574 14.6588 5.02816 14.6643C4.96059 14.6698 4.89295 14.6545 4.83428 14.6205C4.7756 14.5865 4.72868 14.5355 4.69978 14.4742C4.67088 14.4128 4.66136 14.3441 4.6725 14.2773L5.68183 8.59326" stroke="#FF7F32" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M8 9.3335C10.2091 9.3335 12 7.54264 12 5.3335C12 3.12436 10.2091 1.3335 8 1.3335C5.79086 1.3335 4 3.12436 4 5.3335C4 7.54264 5.79086 9.3335 8 9.3335Z" stroke="#FF7F32" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
                <clipPath id="clip0_300_5797">
                <rect width="16" height="16" fill="white"/>
                </clipPath>
            </defs>
            </svg>
            <span className="text-[#ff7f32] text-sm font-bold uppercase tracking-wider">Certificación Garantizada</span>
          </div>
          
          <h2 className="text-4xl md:text-6xl font-black italic text-white uppercase tracking-tighter mb-6">
            Elegir <span className="text-[#459edf]">certificación</span> es elegir <span className="text-[#ff7f32]">confianza</span>
          </h2>
          
          <p className="text-xl text-gray-200 mb-12 leading-relaxed">
           Todos los cascos MRC cuentan con certificaciones internacionales, DOT, ECE o ambas, garantizando tu seguridad en cada kilómetro.
          </p>
          
          <button 
            onClick={() => navigate('/products')}
            className="bg-[#ff7f32] text-white px-10 py-4 rounded-full font-bold italic uppercase tracking-wide shadow-2xl hover:bg-[#e66a20] transition-colors inline-flex items-center gap-2 group"
          >
            Ver Catálogo Certificado
            <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </button>
          
          {/* Stats */}
          <div className="mt-16 pt-8 border-t border-white/20 grid grid-cols-3 gap-8">
            <div>
              <p className="text-4xl font-black text-[#ff7f32] mb-2">100%</p>
              <p className="text-sm text-gray-300 uppercase tracking-wider">Certificado DOT</p>
            </div>
            <div>
              <p className="text-4xl font-black text-[#459edf] mb-2">100%</p>
              <p className="text-sm text-gray-300 uppercase tracking-wider">Certificado ECE 22.06</p>
            </div>
            <div>
              <p className="text-4xl font-black text-white mb-2">3</p>
              <p className="text-sm text-gray-300 uppercase tracking-wider">Años de Garantía</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
